import React from 'react';

import { Button, SVGIcon } from '../../../../../components/custom-essentials';
import { formatDate } from '../../../../../components/functions';
import { TooltipWrapper } from '../../../../../components/display';

export function getColumns(downloadFile, dateEditing){
    const {
        getDraftValue,
        setDraftValue,
        onDateChange,
        onSetToday,
        onClearDate,
        isRowSaving,
        canRemoveLessons,
        onRemoveLesson,
    } = dateEditing || {};

    return [
        {
            dataField: 'combinedSortOrder',
            text: 'Sort Order',
            sort: true,
            sortFunc: (a, b, order) => {
                const swap = order === 'asc' ? -1 : 1;
                const aLPIndex = parseInt(a.split('_')[0]);
                const aItemIndex = parseInt(a.split('_')[1]);
                const bLPIndex = parseInt(b.split('_')[0]);
                const bItemIndex = parseInt(b.split('_')[1]);

                let sortValue = 0;
                if(aLPIndex < bLPIndex) sortValue = -1;
                else if(aLPIndex > bLPIndex) sortValue = 1;
                else {
                    if(aItemIndex < bItemIndex) sortValue = -1;
                    else if(aItemIndex > bItemIndex) sortValue = 1;
                    else sortValue = 0;
                }
                return swap * sortValue;
            }
        },
        {
            dataField: 'name',
            text: 'Name',
            sort: true,
            formatter: (row) => {
                return `${row.lessonObj.lesson_id}: ${row.lessonObj.name}`;
            }
        },
        {
            dataField: 'has_file',
            text: 'Download',
            formatter: (row) => {
                const lesson = { ...row.lessonObj };
                lesson.type = lesson.lesson_id.includes('_EP1') ? 'epLesson' : 'lesson';
                const appendLogo = true;
                if(lesson.has_file){
                    return(
                        <TooltipWrapper
                            tooltipText="View/Download this lesson"
                        >
                            <Button
                                color="lte-mpEGreen"
                                variant="icon"
                                onClick={() => downloadFile(lesson, row.isReview, appendLogo)}
                            >
                                <SVGIcon fileName="print"/>
                            </Button>
                        </TooltipWrapper>
                    );
                } else {
                    return(
                        <Button
                            color="lte-mpLBlue"
                            variant="icon"
                            disabled
                            onClick={() => null}
                        >
                            <TooltipWrapper
                                tooltipText="No file path provided"
                            >
                                <SVGIcon fileName="unlink"/>
                            </TooltipWrapper>
                        </Button>
                    );
                }
            }
        },
        {
            dataField: 'answer_key',
            text: 'Answer Key',
            formatter: (row) => {
                const lesson = { ...row.lessonObj };
                lesson.type = lesson.lesson_id.includes('_EP1') ? 'epAnswerKey' : 'answerKey';
                const appendLogo = false;
                if(lesson.has_answer_key){
                    return(
                        <TooltipWrapper
                            tooltipText="View answer key"
                        >
                            <Button
                                color="lte-mpEGreen"
                                variant="icon"
                                onClick={() => downloadFile(lesson, row.isReview, appendLogo)}
                            >
                                <SVGIcon fileName="answer-key"/>
                            </Button>
                        </TooltipWrapper>
                    );
                } else {
                    return(
                        <Button
                            color="lte-mpLBlue"
                            variant="icon"
                            disabled
                            onClick={() => null}
                        >
                            <TooltipWrapper
                                tooltipText="No answer key link provided"
                            >
                                <SVGIcon fileName="unlink"/>
                            </TooltipWrapper>
                        </Button>
                    );
                }
            }
        },
        {
            dataField: 'date_assigned',
            text: 'Date Assigned',
            sort: true,
            sortFunc: (a, b, order) => {
                const swap = order === 'asc' ? -1 : 1;
                const aDate = new Date(a);
                const bDate = new Date(b);
                return (aDate - bDate) * swap;                
            },
            formatter: (row) => {
                // If date editing is enabled (LP Manager), show inline date input
                if (dateEditing && typeof getDraftValue === 'function') {
                    return (
                        <div className="flex flex-row items-center gap-x-2">
                            <input
                                type="date"
                                className="border border-mpLGrey-4 rounded px-2 py-1 bg-white text-mpDBlue"
                                value={getDraftValue(row, 'date_assigned')}
                                onChange={(e) => {
                                    if (typeof onDateChange === 'function') onDateChange(row, 'date_assigned', e.target.value);
                                }}
                                disabled={typeof isRowSaving === 'function' ? isRowSaving(row) : false}
                            />
                            <Button
                                color="lte-mpLBlue"
                                variant="small"
                                onClick={() => typeof onSetToday === 'function' && onSetToday(row, 'date_assigned')}
                                disabled={typeof isRowSaving === 'function' ? isRowSaving(row) : false}
                            >
                                Today
                            </Button>
                            <Button
                                color="lte-mpLRed"
                                variant="small"
                                onClick={() => typeof onClearDate === 'function' && onClearDate(row, 'date_assigned')}
                                disabled={typeof isRowSaving === 'function' ? isRowSaving(row) : false}
                            >
                                Clear
                            </Button>
                        </div>
                    );
                }
                if(!row.date_assigned) return <div className="text-mpOrange">Never</div>;
                return <div>{formatDate(row.date_assigned)}</div>;
            }
        },
        {
            dataField: 'date_completed',
            text: 'Date Completed',
            sort: true,
            sortFunc: (a, b, order) => {
                const swap = order === 'asc' ? -1 : 1;
                const aDate = new Date(a);
                const bDate = new Date(b);
                return (aDate - bDate) * swap;                
            },
            formatter: (row) => {
                // If date editing is enabled (LP Manager), show inline date input
                if (dateEditing && typeof getDraftValue === 'function') {
                    return (
                        <div className="flex flex-row items-center gap-x-2">
                            <input
                                type="date"
                                className="border border-mpLGrey-4 rounded px-2 py-1 bg-white text-mpDBlue"
                                value={getDraftValue(row, 'date_completed')}
                                onChange={(e) => {
                                    if (typeof onDateChange === 'function') onDateChange(row, 'date_completed', e.target.value);
                                }}
                                disabled={typeof isRowSaving === 'function' ? isRowSaving(row) : false}
                            />
                            <Button
                                color="lte-mpLBlue"
                                variant="small"
                                onClick={() => typeof onSetToday === 'function' && onSetToday(row, 'date_completed')}
                                disabled={typeof isRowSaving === 'function' ? isRowSaving(row) : false}
                            >
                                Today
                            </Button>
                            <Button
                                color="lte-mpLRed"
                                variant="small"
                                onClick={() => typeof onClearDate === 'function' && onClearDate(row, 'date_completed')}
                                disabled={typeof isRowSaving === 'function' ? isRowSaving(row) : false}
                            >
                                Clear
                            </Button>
                        </div>
                    );
                }
                if(!row.date_completed) return <div className="text-mpOrange">Never</div>;
                return <div>{formatDate(row.date_completed)}</div>;
            }
        },
        ...(dateEditing && canRemoveLessons && typeof onRemoveLesson === 'function' ? [{
            dataField: 'remove',
            text: 'Remove',
            formatter: (row) => {
                return (
                    <TooltipWrapper tooltipText="Remove this lesson from the learning plan">
                        <Button
                            color="lte-mpLRed"
                            variant="icon"
                            onClick={() => onRemoveLesson(row)}
                        >
                            <SVGIcon fileName="trash"/>
                        </Button>
                    </TooltipWrapper>
                );
            }
        }] : []),
    ];
}